import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Spin, Alert } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import {
  getDashboardMetric5,
  type DashboardMetric5CycleRiskBreakdown,
} from '@/services/dashboardService';

const { Text } = Typography;

// Fixed colors per requirement
const RISK_HIGH = '#EF4444'; // red
const RISK_MED = '#E6A700'; // yellow/gold
const RISK_LOW = '#22C55E'; // green
const RISK_UNRANKED = '#9CA3AF'; // grey

type Datum = {
  cycleId: string;
  cycleLabel: string; // "A00 Revenue"
  high: number;
  medium: number;
  low: number;
  unranked: number;
  total: number;
};

export type EX1RiskRankingOfProcessesByCycleGroupedHorizontalBarChartProps = {
  height?: number;
  title?: string;
};

const Metric5Tooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  const items = payload
    .filter((p: any) => typeof p?.value === 'number')
    .map((p: any) => ({ name: p.name, value: p.value, color: p.color }));

  const total = items.reduce((s: number, x: any) => s + (x.value ?? 0), 0);

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
        minWidth: 240,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>

      {items.map((x: any) => (
        <div
          key={x.name}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 4,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: x.color,
              display: 'inline-block',
            }}
          />
          <span style={{ flex: 1 }}>{x.name}</span>
          <span style={{ fontWeight: 700 }}>{x.value}</span>
        </div>
      ))}

      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span>Total</span>
        <span style={{ fontWeight: 800 }}>{total}</span>
      </div>

      <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.6)' }}>
        Click a bar to open Exhibit 4 filtered by Cycle + Risk Ranking
      </div>
    </div>
  );
};

export const EX1RiskRankingOfProcessesByCycleGroupedHorizontalBarChart: React.FC<
  EX1RiskRankingOfProcessesByCycleGroupedHorizontalBarChartProps
> = ({ height = 380, title = 'Risk Ranking of Processes by Cycle' }) => {
  const navigate = useNavigate();

  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [raw, setRaw] = useState<DashboardMetric5CycleRiskBreakdown[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRaw([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric5(annualReportId);
        if (!isMounted) return;
        setRaw(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRaw([]);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 5 (Process risk ranking breakdown by cycle).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const data: Datum[] = useMemo(() => {
    return (
      raw
        .map(c => {
          const high = c.highCount ?? 0;
          const medium = c.mediumCount ?? 0;
          const low = c.lowCount ?? 0;
          const unranked = c.unrankedCount ?? 0;

          return {
            cycleId: c.cycleId,
            cycleLabel: `${c.cycleCode} ${c.cycleName}`.trim(),
            high,
            medium,
            low,
            unranked,
            total: high + medium + low + unranked,
          };
        })
        // Sort by total desc to match dashboard expectation
        //.sort((a, b) => b.total - a.total)
    );
  }, [raw]);

  const maxTotal = useMemo(() => {
    return Math.max(0, ...data.map(d => d.high ?? 0),  ...data.map(d => d.medium ?? 0),  ...data.map(d => d.low ?? 0),  ...data.map(d => d.unranked ?? 0));
  }, [data]);

  const xTicks = useMemo(() => {
    // same pattern: 1..maxTotal (forces odd + even tick labels)
    return Array.from({ length: maxTotal }, (_, i) => i + 1);
  }, [maxTotal]);

  const riskTotals = useMemo(() => {
    return data.reduce(
      (acc, d) => {
        acc.high += d.high ?? 0;
        acc.medium += d.medium ?? 0;
        acc.low += d.low ?? 0;
        acc.unranked += d.unranked ?? 0;
        acc.total += (d.high ?? 0) + (d.medium ?? 0) + (d.low ?? 0) + (d.unranked ?? 0);
        return acc;
      },
      { high: 0, medium: 0, low: 0, unranked: 0, total: 0 },
    );
  }, [data]);

  
    const { totalCycles, totalProcesses } = useMemo(() => {
      const totalCycles = data.length;
  
      // safest: sum processCount (already provided by API)
      const totalProcesses = data.reduce(
        (sum, c) => sum + (c.total ?? 0),
        0,
      );
  
      return { totalCycles, totalProcesses };
    }, [data]);
  
    const computedTitle =
      !loading && !errorMessage && data.length > 0
        ? `Risk Ranking of Processes (Total Count: ${totalProcesses}) by Cycle (Total Count: ${totalCycles})`
        : title;

  const isEmpty = !loading && !errorMessage && data.length === 0;

  // NOTE: adjust query keys to match Exhibit 4 filtering
  // If your Exhibit 4 expects ranking values "High|Medium|Low|Unranked" (case sensitive), align here.
  const goToExhibit4 = (
    cycleId: string,
    risk: 'High' | 'Medium' | 'Low' | 'Unranked',
  ) => {
    navigate('/risk-assessment', {
      state: {
        cycleId: encodeURIComponent(cycleId),
        riskRanking: encodeURIComponent(risk),
      },
    });
  };

  const computedHeight = useMemo(() => {
    const rowHeight = 55; // tweak 24–36 based on your font/spacing
    const min = height;
    const needed = data.length * rowHeight + 80; // padding for axes/legend
    return Math.max(min, needed);
  }, [data.length, height]);

  return (
    <Card title={computedTitle} extra={<Text type='secondary'></Text>}>
      <div style={{ height: computedHeight }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 5 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No cycles were returned.'
            showIcon
          />
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart
              data={data}
              layout='vertical'
              margin={{ top: 8, right: 140, bottom: 24, left: 32 }}
              barCategoryGap={14}
              barGap={4}
            >
              <XAxis
                type='number'
                domain={[0, maxTotal]}
                ticks={xTicks}
                allowDecimals={false}
                label={{
                  value: 'Count of Processes',
                  position: 'insideBottom',
                  offset: -10,
                }}
              />
              <YAxis
                type='category'
                dataKey='cycleLabel'
                width={210}
                interval={0}
                tickMargin={8}
                label={{
                  value: 'Cycle Name',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -25,
                }}
              />

              <Tooltip content={<Metric5Tooltip />} />
              <Legend
                layout='vertical'
                align='right'
                verticalAlign='middle'
                wrapperStyle={{ paddingLeft: 16 }}
                formatter={(value: string) => {
                  switch (value) {
                    case 'High':
                      return `High (${riskTotals.high} - ${(riskTotals.high / riskTotals.total * 100).toFixed(0)}%)`;
                    case 'Medium':
                      return `Medium (${riskTotals.medium} - ${(riskTotals.medium / riskTotals.total * 100).toFixed(0)}%)`;
                    case 'Low':
                      return `Low (${riskTotals.low} - ${(riskTotals.low / riskTotals.total * 100).toFixed(0)}%)`;
                    case 'Unranked':
                      return `Unranked (${riskTotals.unranked} - ${(riskTotals.unranked / riskTotals.total * 100).toFixed(0)}%)`;
                    default:
                      return value;
                  }
                }}
              />

              <Bar
                dataKey='high'
                name='High'
                fill={RISK_HIGH}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const cycleId = barData?.payload?.cycleId;
                  if (cycleId) goToExhibit4(cycleId, 'High');
                }}
                style={{ cursor: 'pointer' }}
              />
              <Bar
                dataKey='medium'
                name='Medium'
                fill={RISK_MED}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const cycleId = barData?.payload?.cycleId;
                  if (cycleId) goToExhibit4(cycleId, 'Medium');
                }}
                style={{ cursor: 'pointer' }}
              />
              <Bar
                dataKey='low'
                name='Low'
                fill={RISK_LOW}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const cycleId = barData?.payload?.cycleId;
                  if (cycleId) goToExhibit4(cycleId, 'Low');
                }}
                style={{ cursor: 'pointer' }}
              />
              <Bar
                dataKey='unranked'
                name='Unranked'
                fill={RISK_UNRANKED}
                isAnimationActive={false}
                onClick={(barData: any) => {
                  const cycleId = barData?.payload?.cycleId;
                  if (cycleId) goToExhibit4(cycleId, 'Unranked');
                }}
                style={{ cursor: 'pointer' }}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default EX1RiskRankingOfProcessesByCycleGroupedHorizontalBarChart;
